import { Location } from "@angular/common";
import { HttpResponse, HttpErrorResponse } from "@angular/common/http";
import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { fadeInRightAnimation } from "src/@fury/animations/fade-in-right.animation";
import { fadeInUpAnimation } from "src/@fury/animations/fade-in-up.animation";
import { scaleInAnimation } from "src/@fury/animations/scale-in.animation";
import { ErrorType } from "src/app/enums/error-type.enum";
import { ApiService } from "src/app/services/api.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import endpoints from "src/app/helpers/endpoints";

@Component({
  selector: "fury-add-edit-additional-question",
  templateUrl: "./add-edit-additional-question.component.html",
  styleUrls: ["./add-edit-additional-question.component.scss"],
  animations: [fadeInUpAnimation, fadeInRightAnimation, scaleInAnimation],
})
export class AddEditAdditionalQuestionComponent implements OnInit, OnDestroy {
  additionalQuestionForm!: FormGroup;
  questionId: string;
  questionDetails: any;
  allAdditionalQuestion: any[] = [];
  additionalQuestionType: any[] = [];
  ngUnsubscribe = new Subject<void>();
  constructor(
    public apiService: ApiService,
    private activatedRoute: ActivatedRoute,
    public location: Location
  ) {
    this.questionId = this.activatedRoute.snapshot.params["id"];
    if (this.questionId) {
      this.getQuestionDetails();
    }
  }

  ngOnInit(): void {
    this.formInit();
    this.getAllYesNoTypeAditionalQuestion();
    this.getAllAdditionalQuestionType();

    this.additionalQuestionForm
      .get("isDependent")
      .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value) => {
        const dependentOnQuestionControl = this.additionalQuestionForm.get(
          "dependentOnQuestion"
        );
        const dependentOnAnswerControl =
          this.additionalQuestionForm.get("dependentOnAnswer");
        if (value) {
          dependentOnQuestionControl.setValidators([Validators.required]);
          dependentOnAnswerControl.setValidators([Validators.required]);
        } else {
          dependentOnQuestionControl.clearValidators();
          dependentOnAnswerControl.clearValidators();
        }
        dependentOnQuestionControl.updateValueAndValidity();
        dependentOnAnswerControl.updateValueAndValidity();
      });

    this.additionalQuestionForm
      .get("type")
      .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        const optionsControl = this.additionalQuestionForm.get("options");
        if (res === "Dropdown") {
          optionsControl.setValidators([Validators.required]);
          this.addOption();
        } else {
          optionsControl.clearValidators();
          this.clearOptions();
        }
        optionsControl.updateValueAndValidity();
      });
  }

  formInit() {
    this.additionalQuestionForm = new FormGroup({
      title: new FormControl("", [Validators.required]),
      type: new FormControl("", [Validators.required]),
      show: new FormControl("", [Validators.required]),
      isRequired: new FormControl("", [Validators.required]),
      isDependent: new FormControl(false),
      dependentOnAnswer: new FormControl(""),
      dependentOnQuestion: new FormControl(""),
      options: new FormArray([]),
      yes: new FormControl("", [Validators.min(0)]),
      no: new FormControl("", [Validators.min(0)]),
      questionTypeId: new FormControl(""),
    });
  }

  getQuestionDetails() {
    this.apiService
      .get(
        endpoints.fileAClaim.additionalQuestion.getAdditionalQuestionDetails(
          this.questionId
        )
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: HttpResponse<any>) => {
        if (res?.body) {
          this.questionDetails = res?.body?.additionalQuestion;
          this.additionalQuestionForm.patchValue(res?.body?.additionalQuestion);
          this.additionalQuestionForm.patchValue({
            dependentOnQuestion:
              res?.body?.additionalQuestion?.dependentOnQuestion
                ?.additionalQuestionId,
            isDependent:
              res?.body?.additionalQuestion?.dependentOnQuestion &&
              res?.body?.additionalQuestion?.dependentOnAnswer
                ? true
                : false,
            questionTypeId:
              res.body?.additionalQuestion?.questionType
                ?.additionalQuestionTypeId || "",
          });
          if (res.body.additionalQuestion?.options) {
            let data: any[] = res.body.additionalQuestion?.options;
            data.shift();
            this.additionalQuestionForm.patchValue({
              options: data.forEach((element) => {
                this.options.push(
                  new FormGroup({
                    option: new FormControl(element.option, [
                      Validators.required,
                      Validators.minLength(2),
                    ]),
                    price: new FormControl(element.price),
                  })
                );
              }),
            });
          }
          if (res.body.additionalQuestion?.type === "Yes/No") {
            this.additionalQuestionForm.patchValue({
              yes: res.body.additionalQuestion.reflectedPrice.yes
                ? res.body.additionalQuestion.reflectedPrice.yes
                : "",
              no: res.body.additionalQuestion.reflectedPrice.no
                ? res.body.additionalQuestion.reflectedPrice.no
                : "",
            });
          }
        }
      });
  }
  getAllYesNoTypeAditionalQuestion() {
    this.apiService
      .get(
        endpoints.fileAClaim.additionalQuestion
          .getAllYesNoTypeAdditionalQuestion
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: HttpResponse<any>) => {
        if (res.body) {
          this.allAdditionalQuestion = res.body.additionalQuestions?.data;
        }
      });
  }

  get options(): FormArray {
    return this.additionalQuestionForm.get("options") as FormArray;
  }

  createOptionGroup(): FormGroup {
    return new FormGroup({
      option: new FormControl("", [
        Validators.required,
        Validators.minLength(2),
      ]),
      price: new FormControl(""),
    });
  }

  addOption() {
    this.options.push(this.createOptionGroup());
  }

  removeOption(index: number) {
    this.options.removeAt(index);
  }
  clearOptions() {
    this.options.clear();
  }

  addEdit() {
    if (this.additionalQuestionForm.valid) {
      const data = this.additionalQuestionForm.value;
      if (!this.additionalQuestionForm.value?.isDependent) {
        delete data.dependentOnQuestion;
        delete data.dependentOnAnswer;
      }
      let reflectedPrice = {};
      if (
        this.additionalQuestionForm.value.options.length &&
        this.additionalQuestionForm.value.type === "Dropdown"
      ) {
        this.additionalQuestionForm.value.options.forEach((item: any) => {
          if (item.price) {
            const key = item.option.replace(/\s/g, "_");
            reflectedPrice[key] = item.price;
          }
        });
        data.reflectedPrice = reflectedPrice;
      }
      if (this.additionalQuestionForm.value.type === "Yes/No") {
        if (this.additionalQuestionForm.value.yes) {
          reflectedPrice["yes"] = this.additionalQuestionForm.value.yes;
        }
        if (this.additionalQuestionForm.value.no) {
          reflectedPrice["no"] = this.additionalQuestionForm.value.no;
        }
        data.reflectedPrice = reflectedPrice;
      }

      delete data?.isDependent;
      delete data.yes;
      delete data.no;
      if (!this.questionId) {
        this.apiService
          .post(
            endpoints.fileAClaim.additionalQuestion.createAdditionalQuestion,
            data
          )
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe(
            (res: any) => {
              if (res) {
                this.apiService.alert(res?.body?.message, ErrorType.Success);
                this.location.back();
              }
            },
            (err: HttpErrorResponse) => {
              if (err) {
                this.apiService.alert(err.message, ErrorType.Error);
              }
            }
          );
      } else {
        this.apiService
          .patch(
            endpoints.fileAClaim.additionalQuestion.updateAdditionalQuestion(
              this.questionId
            ),
            data
          )
          .subscribe(
            (res: any) => {
              if (res) {
                this.apiService.alert(res?.body?.message, ErrorType.Success);
                this.location.back();
              }
            },
            (err: HttpErrorResponse) => {
              if (err) {
                this.apiService.alert(err.message, ErrorType.Error);
              }
            }
          );
      }
    } else {
      this.additionalQuestionForm.markAllAsTouched();
    }
  }
  getAllAdditionalQuestionType() {
    this.apiService
      .get(endpoints.fileAClaim.additionalQuestion.getAllAdditionalQuestionType)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          if (res) {
            this.additionalQuestionType = res.body?.additionalQuestion;
          }
        },
        error: (err: HttpErrorResponse) => {
          this.apiService.alert(err.error?.message, ErrorType.Error);
        },
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
